import { Routes } from '@angular/router';
import { authGuard } from './auth/auth.guard';
import { guestGuard } from './auth/guest.guard';
import { ShellComponent } from './layout/shell/shell.component';

import { AccueilComponent } from './pages/accueil/accueil.component';
import { EtablissementComponent } from './pages/etablissement/etablissement.component';
import { PrevisionsComponent } from './pages/previsions/previsions.component';
import { QualiteComponent } from './pages/qualite/qualite.component';
import { SatisfactionComponent } from './pages/satisfaction/satisfaction.component';
import { ActualiteComponent } from './pages/actualite/actualite.component';
import { FilActualiteComponent } from './pages/fil-actualite/fil-actualite.component';
import { StatistiquesComponent } from './pages/statistiques/statistiques.component';
import { HorairesComponent } from './pages/horaires/horaires.component';
import { SupervisionComponent } from './pages/supervision/supervision.component';
import { EquipementsComponent } from './pages/equipements/equipements.component';
import { PilotageComponent } from './pages/pilotage/pilotage.component';
import { PilotagePreventionComponent } from './pages/pilotage-prevention/pilotage-prevention.component';
import { PreventionBesoinsCouvertureComponent } from './pages/prevention-besoins-couverture/prevention-besoins-couverture.component';
import { PreventionBesoinDetailComponent } from './pages/prevention-besoin-detail/prevention-besoin-detail.component';
import { PreventionDemandComponent } from './pages/prevention-demand/prevention-demand.component';
import { ApplicationMobileComponent } from './pages/application-mobile/application-mobile.component';
import { CommPreventionListComponent } from './pages/comm-prevention/comm-prevention-list.component';
import { CommPreventionFormComponent } from './pages/comm-prevention/comm-prevention-form.component';

import { LoginComponent } from './auth/login/login.component';
import { RegisterComponent } from './auth/registrer/register.component';
import { ResetPasswordComponent } from './pages/reset-password/reset-password.component';
import { PilotageFullscreenComponent } from './pages/pilotage-fullscreen/pilotage-fullscreen.component';
import { SiteComponent } from './pages/site/site.component';
import { PreventionBilanCatalogComponent } from './pages/prevention-bilan-catalog/prevention-bilan-catalog.component';
import { PreventionMyOffersComponent } from './pages/prevention-my-offers/prevention-my-offers.component';
import { OfferFormPageComponent } from './pages/prevention-offer-form/offer-form-page.component';
import { PreventionOfferLibraryComponent } from './pages/prevention-offer-library/prevention-offer-library.component';
import { OfferSitesComponent } from './pages/prevention-offer-sites/offer-sites.component';
import { unsavedOfferFormGuard } from './pages/prevention-offer-form/unsaved-offer-form.guard';
import { unsavedOfferSitesGuard } from './pages/prevention-offer-sites/unsaved-offer-sites.guard';
import { defaultRedirectGuard } from './auth/default-redirect.guard';

export const routes: Routes = [
  // Pages invité
  { path: 'login',    canActivate: [guestGuard], component: LoginComponent },
  { path: 'register', canActivate: [guestGuard], component: RegisterComponent },
  { path: 'reset-password', canActivate: [guestGuard], component: ResetPasswordComponent },

  // Pilotage fullscreen (hors shell)
  { path: 'pilotage-fullscreen', canActivate: [authGuard], component: PilotageFullscreenComponent },

  // Shell protégé
  {
    path: '',
    component: ShellComponent,
    canActivate: [authGuard],          // vérifie l’accès au shell
    canActivateChild: [authGuard],     // rejoue le guard sur chaque sous-route
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    children: [
      { path: '', pathMatch: 'full', canActivate: [defaultRedirectGuard], component: AccueilComponent },
      { path: 'accueil',       component: AccueilComponent },
      { path: 'etablissement', component: EtablissementComponent },
      { path: 'previsions',    component: PrevisionsComponent },
      { path: 'satisfaction',  component: SatisfactionComponent },
      { path: 'fil-actualite', component: FilActualiteComponent },
      { path: 'qualite',       component: QualiteComponent },
      { path: 'actualite',     component: ActualiteComponent },
      { path: 'statistiques',  component: StatistiquesComponent },
      { path: 'horaires',      component: HorairesComponent },
      { path: 'supervision',   component: SupervisionComponent },
      { path: 'equipements',    component: EquipementsComponent },
      { path: 'pilotage',        component: PilotageComponent },
      { path: 'pilotage-prevention', component: PilotagePreventionComponent },
      { path: 'pilotage-prevention/besoins', component: PreventionBesoinsCouvertureComponent },
      // `:subjectKey` est une clé OPAQUE de l'API unifiée (`need:<code>` / `catalog:<code>`) : jamais parsée côté front.
      { path: 'pilotage-prevention/besoins/:subjectKey', component: PreventionBesoinDetailComponent },
      { path: 'pilotage-prevention/demande', component: PreventionDemandComponent },
      // Drill-down Groupe -> Site (Ajustement UX/navigation, 2026-09-07) : réutilise SiteComponent
      // tel quel (jamais une seconde Vue Site) via `data.groupContext` -- masque le sélecteur de
      // site, force l'onglet Prévention, affiche un breadcrumb + retour vers /pilotage-prevention.
      // La route standalone 'site' ci-dessous garde son comportement historique inchangé.
      { path: 'pilotage-prevention/sites/:establishmentId', component: SiteComponent, data: { groupContext: true } },
      { path: 'site',            component: SiteComponent },
      // Offre de prévention (Lot 9.0-E.E.C+) : routes plates par convention (aucune imbriquée dans
      // ce repo). IMPORTANT -- toute future route dynamique (ex. 'prevention-offres/:offerId') doit
      // être déclarée APRÈS les segments statiques ('bilans', 'nouvelle', 'bibliotheque'...), jamais
      // avant : le routeur Angular capture la première route qui matche dans l'ordre de déclaration.
      // 'prevention-offres/:offerId/sites' (3 segments) ne peut de toute façon jamais être capturée
      // par 'prevention-offres/:offerId' (2 segments, sans children) -- déclarée avant tout de même,
      // par cohérence défensive avec la règle "plus spécifique d'abord" (Lot 9.0-E.E.F section 4).
      { path: 'prevention-offres', component: PreventionMyOffersComponent },
      { path: 'prevention-offres/bilans', component: PreventionBilanCatalogComponent },
      { path: 'prevention-offres/bibliotheque', component: PreventionOfferLibraryComponent },
      { path: 'prevention-offres/nouvelle', component: OfferFormPageComponent, canDeactivate: [unsavedOfferFormGuard] },
      { path: 'prevention-offres/:offerId/sites', component: OfferSitesComponent, canDeactivate: [unsavedOfferSitesGuard] },
      { path: 'prevention-offres/:offerId', component: OfferFormPageComponent, canDeactivate: [unsavedOfferFormGuard] },
      { path: 'application-mobile', component: ApplicationMobileComponent },
      // "Comm prévention" (Lot V1, 2026-09-08) : créer/diffuser/planifier -- distinct de
      // 'pilotage-prevention/demande' (mesurer/piloter). Routes plates (même convention que
      // prevention-offres ci-dessus) : segment statique 'nouvelle' déclaré AVANT le segment
      // dynamique ':campaignId', jamais l'inverse.
      { path: 'comm-prevention', component: CommPreventionListComponent },
      { path: 'comm-prevention/nouvelle', component: CommPreventionFormComponent },
      { path: 'comm-prevention/:campaignId', component: CommPreventionFormComponent },
    ],
  },

  // Fallback
  { path: '**', redirectTo: '' },
];
